import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/Button';
interface AboutSectionProps {
  condensed?: boolean;
}
export function AboutSection({
  condensed = false
}: AboutSectionProps) {
  const navigate = useNavigate();
  return <section id="about" className="py-8 md:py-24 bg-white relative overflow-hidden" data-id="element-16">
      <div className="absolute top-0 right-0 w-1/2 h-full pattern-dots opacity-20 pointer-events-none" data-id="element-17"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-18">
        {condensed ? <div className="flex flex-col lg:flex-row items-center gap-16" data-id="element-19">
            {/* Text Side */}
            <motion.div initial={{
          opacity: 0,
          x: -50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="w-full lg:w-1/2" data-id="element-20">
              <span className="text-burgundy font-medium tracking-[0.2em] uppercase mb-4 block" data-id="element-21">
                Welcome
              </span>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-jet mb-6" data-id="element-22">
                Welcome to <br data-id="element-23" />
                <span className="text-jet" data-id="element-24">Beni's African Hair Braiding.</span>
              </h2>
              <div className="space-y-4 text-jet/70 text-lg leading-relaxed" data-id="element-25">
                <p data-id="element-26">
                  Where cultural heritage meets contemporary elegance. We blend
                  generations of traditional braiding techniques with modern
                  trends to create protective styles as unique as you are.
                </p>
              </div>
              <div className="mt-8" data-id="element-27">
                <Button variant="outline" size="md" onClick={() => navigate('/about')} data-id="element-28">
                  Read More About Us
                </Button>
              </div>

              <div className="mt-10 grid grid-cols-2 gap-6 border-t border-gray-200 pt-8" data-id="element-29">
                <div data-id="element-30">
                  <h4 className="font-heading text-3xl font-bold text-jet mb-2" data-id="element-31">
                    10+
                  </h4>
                  <p className="text-sm font-medium text-jet/50 uppercase tracking-wider" data-id="element-32">
                    Years Experience
                  </p>
                </div>
                <div data-id="element-33">
                  <h4 className="font-heading text-3xl font-bold text-burgundy mb-2" data-id="element-34">
                    5k+
                  </h4>
                  <p className="text-sm font-medium text-jet/50 uppercase tracking-wider" data-id="element-35">
                    Happy Clients
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Image Side — only in condensed/homepage mode */}
            <motion.div initial={{
          opacity: 0,
          x: 50
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="w-full lg:w-1/2 relative" data-id="element-36">
              <div className="aspect-[4/5] rounded-2xl relative overflow-hidden shadow-2xl" data-id="element-37">
                <img src="https://cdn.magicpatterns.com/uploads/rnb6qywB5pzXQx9DVpmrsv/unnamed.webp" alt="Beautiful braided hair style at Beni's African Hair Braiding" className="w-full h-full object-cover" data-id="element-38" />
              </div>
              <div className="absolute -bottom-4 -right-4 w-24 h-24 bg-burgundy/15 rounded-full -z-10" data-id="element-39"></div>
            </motion.div>
          </div> /* Full About — no oval picture, text-focused layout */ : <div className="max-w-4xl mx-auto" data-id="element-40">
            <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-41">
              <span className="text-burgundy font-medium tracking-[0.2em] uppercase mb-4 block text-center" data-id="element-42">
                Who We Are
              </span>
              <h2 className="text-4xl md:text-5xl font-heading font-bold text-jet mb-8 text-center" data-id="element-43">
                Welcome to Beni's African Hair Braiding
              </h2>

              <div className="space-y-6 text-jet/70 text-lg leading-relaxed" data-id="element-44">
                <p data-id="element-45">
                  Step into a space where cultural heritage meets contemporary
                  elegance. Located at 2768 Crain Hwy, Waldorf, MD 20601, Beni's
                  African Hair Braiding is a sanctuary dedicated to the ancient,
                  beautiful art of protective styling. We take immense pride in
                  our roots, seamlessly blending generations of traditional
                  braiding techniques with modern trends to create styles that
                  are as unique as you are.
                </p>
                <p data-id="element-46">
                  Whether you need a simple haircut or an elaborate new style,
                  our experienced stylists can help you decide on the look for
                  you. From a range of locs, braids &amp; twists to complete
                  hair styling, get it right the first time with our friendly
                  and committed team.
                </p>
              </div>
            </motion.div>

            {/* Why Choose Us */}
            <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} className="mt-20" data-id="element-47">
              <h3 className="text-3xl md:text-4xl font-heading font-bold text-jet mb-10 text-center" data-id="element-48">
                Why Choose Us
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-49">
                <div className="p-8 bg-beige rounded-sm border border-gray-100" data-id="element-50">
                  <h4 className="text-xl font-heading font-bold text-jet mb-3" data-id="element-51">
                    Heritage & Artistry
                  </h4>
                  <p className="text-jet/60 leading-relaxed" data-id="element-52">
                    We honor the rich history of African braiding by preserving
                    authentic methods, while continuously evolving our craft
                    with modern, tension-free techniques that protect your
                    natural hair.
                  </p>
                </div>
                <div className="p-8 bg-beige rounded-sm border border-gray-100" data-id="element-53">
                  <h4 className="text-xl font-heading font-bold text-jet mb-3" data-id="element-54">
                    Skilled Stylists
                  </h4>
                  <p className="text-jet/60 leading-relaxed" data-id="element-55">
                    Our dedicated team of master braiders is passionate about
                    celebrating your individuality, working meticulously to
                    enhance your natural beauty and ensure you leave feeling
                    confident and radiant.
                  </p>
                </div>
                <div className="p-8 bg-beige rounded-sm border border-gray-100" data-id="element-56">
                  <h4 className="text-xl font-heading font-bold text-jet mb-3" data-id="element-57">
                    Walk-ins Welcome
                  </h4>
                  <p className="text-jet/60 leading-relaxed" data-id="element-58">
                    No appointment needed! We welcome walk-ins every day. Just
                    stop by and our stylists will take care of you.
                  </p>
                </div>
                <div className="p-8 bg-beige rounded-sm border border-gray-100" data-id="element-59">
                  <h4 className="text-xl font-heading font-bold text-jet mb-3" data-id="element-60">
                    Affordable Pricing
                  </h4>
                  <p className="text-jet/60 leading-relaxed" data-id="element-61">
                    Quality styles at competitive prices. Plus enjoy $10 off
                    braids $80+ and $20 off braids $180+.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Stats */}
            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-gray-200 pt-10" data-id="element-62">
              <div className="text-center" data-id="element-63">
                <h4 className="font-heading text-3xl font-bold text-jet mb-2" data-id="element-64">
                  10+
                </h4>
                <p className="text-sm font-medium text-jet/50 uppercase tracking-wider" data-id="element-65">
                  Years Experience
                </p>
              </div>
              <div className="text-center" data-id="element-66">
                <h4 className="font-heading text-3xl font-bold text-burgundy mb-2" data-id="element-67">
                  5k+
                </h4>
                <p className="text-sm font-medium text-jet/50 uppercase tracking-wider" data-id="element-68">
                  Happy Clients
                </p>
              </div>
              <div className="text-center" data-id="element-69">
                <h4 className="font-heading text-3xl font-bold text-jet mb-2" data-id="element-70">
                  50+
                </h4>
                <p className="text-sm font-medium text-jet/50 uppercase tracking-wider" data-id="element-71">
                  Styles Offered
                </p>
              </div>
              <div className="text-center" data-id="element-72">
                <h4 className="font-heading text-3xl font-bold text-burgundy mb-2" data-id="element-73">
                  6
                </h4>
                <p className="text-sm font-medium text-jet/50 uppercase tracking-wider" data-id="element-74">
                  Days a Week
                </p>
              </div>
            </div>
          </div>}
      </div>
    </section>;
}